import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Button } from '../../../components/ui/button'
import { Pencil, Users, ArrowRight } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog'
import { Label } from '../../../components/ui/label'

interface CayuProvidedSecret {
  id: number
  service_name: string
  secret_name: string
  projects_count: number
  organizations_count: number
  created_at: string
  updated_at: string
}

interface Organization {
  id: number
  name: string
  projects_count: number
}

interface AdminCayuProvidedSecretsShowProps {
  cayu_provided_secret: CayuProvidedSecret
  organizations: Organization[]
  other_secrets: Array<{ id: number; secret_name: string }>
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
}

const SERVICE_ICONS: Record<string, string> = {
  openai: '🤖',
  anthropic: '🧠',
  google_gemini: '✨',
  sendgrid: '📧',
}

const SERVICE_NAMES: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google_gemini: 'Google Gemini',
  sendgrid: 'SendGrid',
}

function Show({ cayu_provided_secret, organizations, other_secrets }: AdminCayuProvidedSecretsShowProps) {
  const { t } = useTranslation('admin')
  const [showMigrateDialog, setShowMigrateDialog] = useState(false)
  const [selectedOrgIds, setSelectedOrgIds] = useState<number[]>([])
  const [targetKeyId, setTargetKeyId] = useState<number | null>(
    other_secrets.length > 0 ? other_secrets[0].id : null
  )

  const handleMigrate = () => {
    if (selectedOrgIds.length === 0) {
      alert(t('cayuProvidedKeys.migrate.errors.selectOrg'))
      return
    }

    if (!targetKeyId) {
      alert(t('cayuProvidedKeys.migrate.errors.selectKey'))
      return
    }

    router.post(
      `/admin/cayu_provided_secrets/${cayu_provided_secret.id}/migrate_organizations`,
      {
        target_cayu_provided_secret_id: targetKeyId,
        organization_ids: selectedOrgIds,
      },
      {
        onSuccess: () => {
          setShowMigrateDialog(false)
          setSelectedOrgIds([])
        },
        onError: (errors) => {
          console.error('Migration error:', errors)
        }
      }
    )
  }

  const toggleOrgSelection = (orgId: number) => {
    setSelectedOrgIds(prev =>
      prev.includes(orgId)
        ? prev.filter(id => id !== orgId)
        : [...prev, orgId]
    )
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: 'Cayu Provided Keys', href: '/admin/cayu_provided_secrets' },
          { label: cayu_provided_secret.secret_name },
        ]}
      />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6 max-w-4xl">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{SERVICE_ICONS[cayu_provided_secret.service_name]}</span>
                    <h1 className="text-3xl font-bold text-foreground">
                      {cayu_provided_secret.secret_name}
                    </h1>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('cayuProvidedKeys.show.apiKeyLabel', { service: SERVICE_NAMES[cayu_provided_secret.service_name] })}
                  </p>
                </div>
                <Button
                  onClick={() => router.visit(`/admin/cayu_provided_secrets/${cayu_provided_secret.id}/edit`)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  {t('cayuProvidedKeys.actions.edit')}
                </Button>
              </div>

              {/* Key Info */}
              <div className="bg-card rounded-lg border border-border p-6 space-y-4 mb-6">
                <div className="space-y-2">
                  <Label>{t('cayuProvidedKeys.form.service')}</Label>
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                    <span>{SERVICE_ICONS[cayu_provided_secret.service_name]}</span>
                    <span className="font-medium">
                      {SERVICE_NAMES[cayu_provided_secret.service_name]}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('cayuProvidedKeys.form.keyName')}</Label>
                  <div className="p-3 bg-muted rounded-md">
                    <span className="font-medium">{cayu_provided_secret.secret_name}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('cayuProvidedKeys.table.created')}</Label>
                    <div className="text-sm text-muted-foreground">
                      {new Date(cayu_provided_secret.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('cayuProvidedKeys.show.lastUpdated')}</Label>
                    <div className="text-sm text-muted-foreground">
                      {new Date(cayu_provided_secret.updated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Migration Section */}
              {organizations.length > 0 && other_secrets.length > 0 && (
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="mb-4">
                    <h2 className="text-lg font-semibold text-foreground mb-1">{t('cayuProvidedKeys.migrate.title')}</h2>
                    <p className="text-sm text-muted-foreground">
                      {t('cayuProvidedKeys.migrate.description')}
                    </p>
                  </div>

                  {/* Usage Stats */}
                  <div className="mb-3 p-2.5 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-foreground">
                          {cayu_provided_secret.organizations_count}
                        </span>
                        <span className="text-muted-foreground">{t('cayuProvidedKeys.usage.organizations')}</span>
                      </div>
                      <span className="text-muted-foreground">-</span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-foreground">
                          {cayu_provided_secret.projects_count}
                        </span>
                        <span className="text-muted-foreground">{t('cayuProvidedKeys.usage.projects')}</span>
                      </div>
                    </div>
                  </div>

                  <Button onClick={() => setShowMigrateDialog(true)} variant="outline">
                    <Users className="w-4 h-4 mr-2" />
                    {t('cayuProvidedKeys.migrate.button')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Migration Dialog */}
      <AlertDialog open={showMigrateDialog} onOpenChange={setShowMigrateDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('cayuProvidedKeys.migrate.dialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('cayuProvidedKeys.migrate.dialogDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 my-4">
            {/* Target Key Selection */}
            <div className="space-y-2">
              <Label htmlFor="target-key">{t('cayuProvidedKeys.migrate.targetKey')}</Label>
              <select
                id="target-key"
                value={targetKeyId || ''}
                onChange={(e) => setTargetKeyId(Number(e.target.value))}
                className="w-full px-3 py-2 border rounded-md bg-white"
              >
                {other_secrets.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.secret_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Organization Selection */}
            <div className="space-y-2">
              <Label>{t('cayuProvidedKeys.migrate.selectOrgs')} ({t('cayuProvidedKeys.migrate.selected', { count: selectedOrgIds.length })})</Label>

              <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
                {organizations.map((org) => (
                  <label
                    key={org.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrgIds.includes(org.id)}
                      onChange={() => toggleOrgSelection(org.id)}
                      className="w-4 h-4"
                    />
                    <div className="flex-1">
                      <div className="font-medium">{org.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {t('cayuProvidedKeys.usage.projects', { count: org.projects_count })}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Preview */}
            {selectedOrgIds.length > 0 && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 text-blue-700">
                  <ArrowRight className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {t('cayuProvidedKeys.migrate.preview', { count: selectedOrgIds.length, target: other_secrets.find(k => k.id === targetKeyId)?.secret_name })}
                  </span>
                </div>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>{t('cayuProvidedKeys.actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleMigrate} disabled={selectedOrgIds.length === 0}>
              {t('cayuProvidedKeys.migrate.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

Show.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Show
